"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const OnboardingContext = createContext({
  onboardings: [],
});

export const OnboardingProvider = ({ children }) => {
  const [onboardings, setOnboardings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // ==========================
  // Fetch Onboardings
  // ==========================
  const fetchOnboardings = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/onboarding`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch onboarding records");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setOnboardings(data.data || []);
      } else {
        setError(data.message || "Failed to fetch onboarding records");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch onboarding records");
    } finally {
      setLoading(false);
    }
  };

  // ==========================
  // Get Single Onboarding
  // ==========================
  const getOnboarding = async (id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/onboarding/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to fetch onboarding",
        };
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to fetch onboarding",
      };
    }
  };

  // ==========================
  // Create Onboarding
  // ==========================
  const createOnboarding = async (payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/onboarding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to create onboarding",
        };
      }

      if (data.status === 201) {
        await fetchOnboardings();

        toast.success(data.message || "Employee onboarded successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create onboarding",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to create onboarding",
      };
    }
  };

  // ==========================
  // Update Onboarding
  // ==========================
  const updateOnboarding = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/onboarding/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to update onboarding",
        };
      }

      if (data.status === 200) {
        await fetchOnboardings();

        toast.success(data.message || "Onboarding updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update onboarding",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update onboarding",
      };
    }
  };

  // ==========================
  // Delete Onboarding
  // ==========================
  const deleteOnboarding = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/onboarding/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id,
        }),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to delete onboarding",
        };
      }

      if (data.status === 200) {
        await fetchOnboardings();

        toast.success(data.message || "Onboarding deleted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete onboarding",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to delete onboarding",
      };
    }
  };

  useEffect(() => {
    const token = getToken();

    if (token) {
      fetchOnboardings();
    }
  }, []);

  return (
    <OnboardingContext.Provider
      value={{
        onboardings,
        loading,
        error,
        fetchOnboardings,
        getOnboarding,
        createOnboarding,
        updateOnboarding,
        deleteOnboarding,
        setOnboardings,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboardings = () => useContext(OnboardingContext);